import { useMutation, useQueryClient } from "@tanstack/react-query";
import { iField, Integration } from "/app/src/models";
import { iFieldService } from "/app/src/services";
import { Formik, FormikHelpers, FormikProps } from "formik";
import { Form, Input, SubmitButton, Switch } from "formik-antd";
import { useCallback } from "react";
import { Button } from "@heroui/react";
import { IconBuilder } from "/app/src/components/icons/IconBuilder";
import { handlePromiseError } from "/app/src/helpers/api";
import { useTranslation } from "react-i18next";
import { simpleSchemaBuilder } from "/app/src/helpers";

interface FormValues {
  name: string;
  values: string;
  required: boolean;
}

/**
 * Formats form values into a standardized object.
 * @param {FormValues} values - The input form values.
 * @returns {Object} The formatted form object.
 */
function formatForm(values: FormValues) {
  return {
    name: values.name,
    values: values.values,
    required: values.required,
  };
}

/**
 * Renders a form for editing order fields.
 * @param {Object} param0 - The function parameters.
 * @param {Integration} param0.integration - The integration object.
 * @param {iField} param0.field - The field object to be edited.
 * @returns {JSX.Element} The rendered order fields editing form.
 */
export default function EditOrderFieldsForm({
  integration,
  field,
  type,
  fieldOptions,
}: {
  integration: Integration;
  field: iField;
  type: "order" | "line";
  fieldOptions: Array<{ label: string; value: string }>;
}) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { mutateAsync: updateField } = useMutation({
    mutationFn: (field: iField) =>
      iFieldService.updateSingle(field.id, field).then(handlePromiseError),
    onSuccess: (response) => {
      queryClient.setQueryData(
        [`${type}Fields`, integration.id],
        (oldData: { fields: iField[] }) => {
          const updatedField = response.field;
          return {
            fields: oldData.fields.map((field) =>
              field.id === updatedField.id ? updatedField : field,
            ),
          };
        },
      );
    },
  });

  const { mutateAsync: deleteField } = useMutation({
    mutationFn: (field: iField) =>
      iFieldService.deleteSingle(field.id).then(handlePromiseError),
    onSuccess: () => {
      queryClient.setQueryData(
        [`${type}Fields`, integration.id],
        (oldData: { fields: iField[] }) => {
          return {
            fields: oldData.fields.filter((f) => f.id !== field.id),
          };
        },
      );
    },
  });

  /**
   * Handles deletion of a field.
   */
  const deleteFieldHandler = useCallback(() => {
    deleteField(field);
  }, [deleteField, field]);

  /**
   * Handles form submission for updating the field.
   * @param {FormValues} values - The form values.
   * @param {FormikHelpers<FormValues>} actions - Helper functions provided by Formik.
   * @returns {Promise<void>} A promise that resolves when the update is complete.
   */
  const handleSubmit = useCallback(
    async (values: FormValues, actions: FormikHelpers<FormValues>) => {
      await updateField({
        id: field.id,
        ...formatForm(values),
      }).then(() => {
        actions.resetForm();
      });
    },
    [updateField, field.id],
  );

  /**
   * Retrieves the label for a given field name.
   * @param {string} fieldName - The name of the field.
   * @returns {string} The label for the provided field name.
   */
  const getFieldLabel = useCallback(
    (fieldName: string) => {
      const field = fieldOptions.find((option) => option.value === fieldName);
      return field ? field.label : fieldName;
    },
    [fieldOptions],
  );

  /**
   * Renders the form layout for editing a field.
   * @param {FormikProps<FormValues>} props - The Formik properties.
   * @returns {JSX.Element} The JSX element representing the form.
   */
  const editFieldForm: (props: FormikProps<FormValues>) => JSX.Element =
    useCallback(
      ({ isSubmitting, dirty, values, isValid }) => (
        <Form>
          <div className="grid grid-cols-3 gap-x-6 gap-y-4 my-3">
            <div className="text-medium flex">
              {getFieldLabel(values?.name)}
            </div>
            <div className="w-[300px]">
              <Form.Item name="values" hasFeedback={false}>
                <Input name="values" size="large" />
              </Form.Item>
            </div>
            <div className="flex justify-between items-start">
              {field?.name !== "name" && field?.name !== "materialName" ? (
                <Form.Item name="required" hasFeedback={false} className="!m-0">
                  <Switch
                    name="required"
                    className="!m-0"
                    aria-label="Required field"
                  />
                </Form.Item>
              ) : (
                <div />
              )}
              <div className="flex items-start gap-2">
                <SubmitButton disabled={isSubmitting || !dirty || !isValid}>
                  {t("translation:save")}
                </SubmitButton>
                <Button
                  onPress={deleteFieldHandler}
                  radius="none"
                  color="default"
                  size="sm"
                  variant="bordered"
                  isIconOnly
                  className="border-error-default bg-white"
                  aria-label="Delete field"
                >
                  <IconBuilder size={18} color="#e00000" icon="Delete" />
                </Button>
              </div>
            </div>
          </div>
        </Form>
      ),
      [deleteFieldHandler, field?.name, getFieldLabel, t],
    );

  return (
    <div>
      <Formik
        key={field?.id}
        initialValues={{
          name: field?.name,
          values: field?.values,
          required: field?.required,
        }}
        validationSchema={simpleSchemaBuilder([
          { name: "values", type: "string", required: true },
        ])}
        onSubmit={handleSubmit}
        component={editFieldForm}
        enableReinitialize
      />
    </div>
  );
}
